import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BookmarkPlus, ChevronLeft, Edit3, ExternalLink, Save, Trash2, X } from 'lucide-react';
import B2BLayout from '../../../components/b2b/B2BLayout';
import { PageLeaveConfirmModal, Textarea, Toast } from '../../../components/ui';
import {
  ErrorBanner,
  normalizePersonaTag,
  PersonaAvatar,
  PersonaTagChip,
  PersonaState,
  PERSONA_TAG_MAX_LENGTH,
  getPersonaMeta,
} from '../components/PersonaShared';
import {
  asDetailText,
  buildEditableDomainCharacteristicItems,
  buildPersonaDetail,
  extractDomainCharacteristicsMap,
  extractDomainLabelMap,
} from '../services/personaDetail';
import { personaApi } from '../services/personaService';
import '../styles/persona.scss';

const blankEditForm = {
  name: '',
  tag: '',
  title: '',
  gender: '',
  age: '',
  personality: '',
  language: 'ko',
  folder_id: '',
  profile: {},
  telecom_profile: {},
  telecomBehaviorDimensions: {},
  telecomBehaviorScores: [],
  domainVariables: {},
};

const DETAIL_TEXTAREA_MAX_LENGTH = 1000;
const DETAIL_BASIC_INPUT_MAX_LENGTH = 20;

const TELECOM_EDIT_SECTIONS = [
  {
    key: 'brandRetention',
    title: '브랜드 유지 성향',
    description: '브랜드를 유지하거나 프리미엄 인프라와 혜택을 중시하는 기준입니다.',
    items: [
      ['브랜드 유지 성향', 'brandRetentionTendency'],
      ['프리미엄 인프라 및 혜택 지향', 'premiumInfraBenefitOrientation'],
    ],
  },
  {
    key: 'optimizationResource',
    title: '최적화 리소스 투입',
    description: '비용 절감과 시간/노력 투입 사이에서 어떤 선택을 하는지 보여줍니다.',
    items: [
      ['최적화 리소스 투입(금전 ↔ 시간/노력)', 'optimizationResourceInvestment'],
      ['비용 지불 저항선', 'paymentResistanceLine'],
    ],
  },
  {
    key: 'informationControl',
    title: '정보 탐색 및 통제 욕구',
    description: '정보를 탐색하고 문제를 해결할 때 얼마나 직접 통제하려 하는지 나타냅니다.',
    items: [
      ['정보 탐색 양식', 'informationExplorationStyle'],
      ['문제 해결 경로의 자율성', 'problemSolvingAutonomy'],
    ],
  },
  {
    key: 'digitalAiOpenness',
    title: '디지털 및 AI 개방성',
    description: 'AI, 공급자 추천, 개인화 데이터 제공을 받아들이는 태도입니다.',
    items: [
      ['AI 및 공급자 신뢰도', 'aiProviderTrust'],
      ['개인화 수용 및 정보제공 범위', 'personalizationDataSharingScope'],
    ],
  },
  {
    key: 'telecomLifeCharacteristics',
    title: '변수에 따른 통신 생활 특성',
    description: '가구 의사결정, 상품 이해도, 실제 통신 서비스 이용 맥락을 연결합니다.',
    items: [
      ['가구 내 및 통신 의사결정 주도권', 'householdDecisionLeadership'],
      ['상품 및 서비스 이해도', 'productServiceUnderstanding'],
      ['통신 서비스 이용 맥락', 'telecomServiceUsageContext'],
    ],
  },
];

const TELECOM_DNA_EDIT_SECTIONS = TELECOM_EDIT_SECTIONS.filter((section) => section.key !== 'telecomLifeCharacteristics');
const TELECOM_LIFE_EDIT_SECTION = TELECOM_EDIT_SECTIONS.find((section) => section.key === 'telecomLifeCharacteristics');

const asInputValue = (value) => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (typeof value === 'object') return value.summary || value.description || value.value || JSON.stringify(value);
  return String(value);
};

const isExploreCatalogPersona = (persona) => {
  const sourceType = String(persona?.sourceType || persona?.source_type || '').toLowerCase();
  return sourceType === 'explore';
};

const clampTelecomScore = (value, maxScore = 5) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 3;
  return Math.max(1, Math.min(maxScore, Math.round(numeric)));
};

const normalizeTelecomScoreList = (scores) => (
  Array.isArray(scores)
    ? scores.map((score) => {
      const maxScore = Number(score?.maxScore || score?.max_score || 5) || 5;
      return {
        ...score,
        maxScore,
        score: clampTelecomScore(score?.score, maxScore),
        rationale: score?.rationale || score?.basis || '',
        evidence: Array.isArray(score?.evidence)
          ? score.evidence.filter(Boolean)
          : Array.isArray(score?.evidenceSnippets)
            ? score.evidenceSnippets.filter(Boolean)
            : Array.isArray(score?.evidence_snippets)
              ? score.evidence_snippets.filter(Boolean)
              : [],
      };
    }).filter((score) => score?.key)
    : []
);

const compactObject = (value) => Object.fromEntries(
  Object.entries(value || {}).filter(([, entry]) => entry !== undefined && entry !== null)
);

const getDimensionGroup = (dimensions, groupKey) => {
  const snakeKey = groupKey.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  return dimensions?.[groupKey] || dimensions?.[snakeKey] || {};
};

const getDimensionValue = (dimensions, groupKey, fieldKey) => {
  const group = getDimensionGroup(dimensions, groupKey);
  const snakeKey = fieldKey.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  return group?.[fieldKey] ?? group?.[snakeKey] ?? '';
};

const hasDetailValue = (value) => String(asInputValue(value)).trim().length > 0;

const visibleEditValues = (form) => {
  const detailMode = buildPersonaDetail(form)?.detailMode;
  const isUnemployed = String(form.title || '').trim() === '무직';
  const telecomDnaValues = TELECOM_DNA_EDIT_SECTIONS.flatMap((section) => (
    section.items.map(([, fieldKey]) => (
      getDimensionValue(form.telecomBehaviorDimensions, section.key, fieldKey)
    ))
  ));
  const telecomLifeValues = TELECOM_LIFE_EDIT_SECTION
    ? TELECOM_LIFE_EDIT_SECTION.items.map(([, fieldKey]) => (
      getDimensionValue(form.telecomBehaviorDimensions, TELECOM_LIFE_EDIT_SECTION.key, fieldKey)
    ))
    : [];

  const commonValues = [
    form.name,
    form.tag,
    form.age,
    form.profile?.generation,
    form.gender,
    form.currentCity,
    form.currentCountry,
    ...(isUnemployed ? [] : [form.profile?.income]),
    form.personality,
    form.profile?.behaviours,
    form.profile?.motivation,
    form.profile?.attitudes,
    form.culturalBackground,
    form.profile?.upbringing,
    form.profile?.biography,
    form.profile?.residence_history,
    form.profile?.interests,
    form.socialContext,
    form.profile?.preferences,
    form.profile?.demeanour,
    form.profile?.quote,
  ];
  const jobValues = isUnemployed
    ? [form.title]
    : [form.title, form.profile?.sector, form.roleArea];
  const domainValues = detailMode === 'domain'
    ? Object.values(extractDomainCharacteristicsMap(form))
    : [];

  return [
    ...commonValues,
    ...jobValues,
    ...(detailMode === 'telecom' ? [...telecomDnaValues, ...telecomLifeValues] : []),
    ...domainValues,
  ];
};

const getResidenceHistory = (persona, profile, detailProfile) =>
  persona?.locations ??
  profile?.locations ??
  detailProfile?.residence_history ??
  profile?.residence_history ??
  '';

const normalizePersonaForEdit = (persona) => {
  if (!persona) return blankEditForm;
  const profile = persona.profile || {};
  const detailProfile = persona.detail_profile || profile.detail_profile || {};
  const telecom = persona.telecom_profile || {};
  const residenceHistory = getResidenceHistory(persona, profile, detailProfile);
  return {
    ...blankEditForm,
    ...persona,
    tag: normalizePersonaTag(persona.tag),
    age: persona.age ?? '',
    folder_id: persona.folder_id || persona.folderId || '',
    currentCity: persona.currentCity ?? profile.currentCity ?? profile.location ?? '',
    currentCountry: persona.currentCountry ?? profile.currentCountry ?? '',
    roleArea: persona.roleArea ?? profile.roleArea ?? profile.role ?? '',
    roleLevel: persona.roleLevel ?? profile.roleLevel ?? profile.position ?? '',
    socialContext: persona.socialContext ?? profile.socialContext ?? profile.relationships ?? '',
    culturalBackground: persona.culturalBackground ?? profile.culturalBackground ?? profile.cultural_background ?? '',
    telecomBehaviorDimensions: persona.telecomBehaviorDimensions || telecom.telecomBehaviorDimensions || {},
    telecomBehaviorScores: normalizeTelecomScoreList(persona.telecomBehaviorScores || persona.telecom_behavior_scores),
    domainVariables: persona.domainVariables || persona.domain_variables || {},
    profile: {
      ...profile,
      generation: persona.generation ?? profile.generation ?? '',
      income: persona.income ?? profile.income ?? profile.annual_income ?? '',
      sector: persona.sector ?? profile.sector ?? profile.industry ?? '',
      organisation: persona.organisation ?? profile.organisation ?? profile.company ?? '',
      motivation: persona.motivation ?? profile.motivation ?? '',
      attitudes: persona.attitudes ?? profile.attitudes ?? profile.values ?? '',
      biography: persona.biography ?? profile.biography ?? profile.history ?? '',
      demeanour: persona.demeanour ?? profile.demeanour ?? profile.speech_style ?? '',
      interests: persona.interests ?? profile.interests ?? '',
      upbringing: persona.upbringing ?? profile.upbringing ?? profile.growth_background ?? '',
      preferences: persona.preferences ?? profile.preferences ?? '',
      behaviours: persona.behaviours ?? profile.behaviours ?? profile.behavior_pattern ?? '',
      locations: residenceHistory,
      residence_history: residenceHistory,
      quote: persona.quote ?? profile.quote ?? '',
    },
    telecom_profile: telecom,
  };
};

const buildUpdatePayload = (form) => {
  const residenceHistory = form.profile?.residence_history ?? form.profile?.locations ?? '';
  const profile = compactObject({
    ...(form.profile || {}),
    locations: residenceHistory,
    residence_history: residenceHistory,
    age: form.age,
    gender: form.gender,
    job: form.title,
    currentCity: form.currentCity,
    currentCountry: form.currentCountry,
    location: form.currentCity,
    sector: form.sector ?? form.profile?.sector,
    industry: form.sector ?? form.profile?.sector,
    organisation: form.organisation ?? form.profile?.organisation,
    company: form.organisation ?? form.profile?.organisation,
    roleArea: form.roleArea,
    role: form.roleArea,
    roleLevel: form.roleLevel,
    position: form.roleLevel,
    personality: form.personality,
    culturalBackground: form.culturalBackground,
    cultural_background: form.culturalBackground,
    socialContext: form.socialContext,
    relationships: form.socialContext,
    values: form.attitudes ?? form.profile?.attitudes,
    speech_style: form.demeanour ?? form.profile?.demeanour,
  });
  const telecomProfile = compactObject({
    ...(form.telecom_profile || {}),
    telecomBehaviorDimensions: form.telecomBehaviorDimensions || {},
  });
  const telecomBehaviorScores = normalizeTelecomScoreList(form.telecomBehaviorScores);

  return {
    name: form.name,
    tag: normalizePersonaTag(form.tag),
    gender: form.gender,
    title: form.title,
    personality: form.personality,
    language: form.language || 'ko',
    folder_id: form.folder_id ? Number(form.folder_id) : null,
    age: form.age === '' ? null : Number(form.age),
    generation: form.profile?.generation || '',
    income: form.profile?.income || '',
    sector: form.profile?.sector || '',
    organisation: form.profile?.organisation || '',
    role_area: form.roleArea || '',
    role_level: form.roleLevel || '',
    attitudes: form.profile?.attitudes || '',
    biography: form.profile?.biography || '',
    demeanour: form.profile?.demeanour || '',
    interests: form.profile?.interests || '',
    motivation: form.profile?.motivation || '',
    upbringing: form.profile?.upbringing || '',
    preferences: form.profile?.preferences || '',
    social_context: form.socialContext || '',
    cultural_background: form.culturalBackground || '',
    quote: form.profile?.quote || '',
    behaviours: form.profile?.behaviours || '',
    current_city: form.currentCity || '',
    current_country: form.currentCountry || '',
    locations: residenceHistory || null,
    telecom_behavior_dimensions: form.telecomBehaviorDimensions || {},
    telecom_behavior_scores: telecomBehaviorScores.length ? telecomBehaviorScores : undefined,
    domain_variables: form.domainVariables || form.domain_variables || {},
    profile,
    telecom_profile: telecomProfile,
  };
};

function PersonaDetailPage({ mode = 'detail', exploreCatalog = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { personaId } = useParams();
  const isEditMode = mode === 'edit';
  const allowNavigationRef = useRef(false);
  const [persona, setPersona] = useState(null);
  const [form, setForm] = useState(blankEditForm);
  const [baselinePayload, setBaselinePayload] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [pendingEditLeave, setPendingEditLeave] = useState(null);
  const [deletingPersona, setDeletingPersona] = useState(false);
  const [error, setError] = useState('');
  const [activeTelecomScoreKey, setActiveTelecomScoreKey] = useState('brandRetention');
  const [toast, setToast] = useState({ isOpen: false, type: 'success', message: '' });
  const [adopting, setAdopting] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ isOpen: true, type, message });
  }, []);

  useEffect(() => {
    allowNavigationRef.current = false;
  }, [isEditMode]);

  const returnTarget = useMemo(() => {
    const from = location.state?.from;
    if (from?.pathname === '/personas/explore') {
      return { pathname: '/personas/explore', state: from.state };
    }
    if (from?.pathname === '/personas') {
      return { pathname: '/personas', state: from.activeFolder ? { activeFolder: from.activeFolder } : undefined };
    }
    return { pathname: '/personas' };
  }, [location.state]);

  const navigateToReturnTarget = useCallback(() => {
    navigate(returnTarget.pathname, returnTarget.state ? { state: returnTarget.state } : undefined);
  }, [navigate, returnTarget]);

  const resetEditFormFromPersona = useCallback((source) => {
    if (!source) return;
    const normalized = normalizePersonaForEdit(source);
    setForm(normalized);
    setBaselinePayload(JSON.stringify(buildUpdatePayload(normalized)));
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const personaData = exploreCatalog
        ? await personaApi.getExplorePersona(personaId)
        : await personaApi.getPersona(personaId);
      setPersona(personaData);
      resetEditFormFromPersona(personaData);
    } catch (err) {
      setError(err.message || 'Persona 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [exploreCatalog, personaId, resetEditFormFromPersona]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!persona) return;
    resetEditFormFromPersona(persona);
  }, [isEditMode, persona, resetEditFormFromPersona]);

  const isExplorePersona = exploreCatalog || isExploreCatalogPersona(persona);
  const adoption = persona?.adoption || {};
  const adoptedPersonaId = adoption.personaId || adoption.persona_id || null;
  const isAdopted = Boolean(adoption.adopted && adoptedPersonaId);

  useEffect(() => {
    if (!isExplorePersona || !isEditMode) return;
    navigate(exploreCatalog ? `/personas/explore/${personaId}` : `/personas/${personaId}`, {
      replace: true,
      state: location.state || { from: { pathname: '/personas/explore' } },
    });
  }, [exploreCatalog, isEditMode, isExplorePersona, location.state, navigate, personaId]);

  const handleDelete = () => {
    if (isExplorePersona) return;
    setShowDeleteModal(true);
  };

  const handleAdopt = async () => {
    if (!isExplorePersona || adopting) return;
    setAdopting(true);
    setError('');
    try {
      const result = await personaApi.adoptExplorePersona(personaId);
      const nextId = result?.persona?.id;
      setPersona((current) => (
        current
          ? {
              ...current,
              adoption: {
                adopted: true,
                personaId: nextId,
                persona_id: nextId,
              },
            }
          : current
      ));
      showToast(
        result?.alreadyAdopted
          ? '이미 내 퍼소나 리스트에 담긴 퍼소나입니다.'
          : '내 퍼소나 리스트에 담았습니다.\n테스트에서 바로 활용할 수 있습니다.'
      );
    } catch (err) {
      setError(err.message || '퍼소나 담기에 실패했습니다.');
    } finally {
      setAdopting(false);
    }
  };

  const isDirty = isEditMode && baselinePayload && JSON.stringify(buildUpdatePayload(form)) !== baselinePayload;
  const requestEditLeave = useCallback((action = { type: 'return' }) => {
    if (!isEditMode || !isDirty || allowNavigationRef.current) return true;
    setPendingEditLeave(action);
    return false;
  }, [isDirty, isEditMode]);

  const confirmEditLeave = useCallback((targetPath, proceed) => (
    requestEditLeave({ type: 'navigate', targetPath, proceed })
  ), [requestEditLeave]);

  const handleCancelPendingEditLeave = useCallback(() => {
    if (pendingEditLeave?.restoreHistoryOnCancel) {
      window.history.pushState({ personaEditGuard: true }, '', window.location.href);
    }
    setPendingEditLeave(null);
  }, [pendingEditLeave]);

  const handleConfirmPendingEditLeave = useCallback(() => {
    const action = pendingEditLeave;
    setPendingEditLeave(null);
    if (!action) return;
    allowNavigationRef.current = true;

    if (typeof action.proceed === 'function') {
      action.proceed();
      return;
    }

    if (action.type === 'history') {
      window.history.back();
      return;
    }

    if (action.type === 'return') {
      navigateToReturnTarget();
      return;
    }

    navigate(action.targetPath || '/personas');
  }, [navigate, navigateToReturnTarget, pendingEditLeave]);

  useEffect(() => {
    if (!isEditMode || !isDirty) return undefined;
    window.history.pushState({ personaEditGuard: true }, '', window.location.href);
    const handlePopState = () => {
      if (allowNavigationRef.current) return;
      setPendingEditLeave({
        type: 'history',
        restoreHistoryOnCancel: true,
      });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty, isEditMode]);

  const navigateWithoutBlock = useCallback((to, options) => {
    allowNavigationRef.current = true;
    navigate(to, options);
  }, [navigate]);

  const handleConfirmDelete = async () => {
    if (deletingPersona) return;
    setDeletingPersona(true);
    setError('');
    try {
      await personaApi.deletePersona(personaId);
      navigateWithoutBlock('/personas', {
        state: { toast: { message: '선택한 퍼소나를 삭제했습니다.' } },
      });
    } catch (err) {
      setError(err.message || 'Persona 삭제에 실패했습니다.');
    } finally {
      setDeletingPersona(false);
    }
  };

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const setProfileField = (field, value) => {
    setForm((current) => ({
      ...current,
      profile: {
        ...(current.profile || {}),
        [field]: value,
      },
    }));
  };

  const setDimensionField = (groupKey, fieldKey, value) => {
    setForm((current) => ({
      ...current,
      telecomBehaviorDimensions: {
        ...(current.telecomBehaviorDimensions || {}),
        [groupKey]: {
          ...getDimensionGroup(current.telecomBehaviorDimensions, groupKey),
          [fieldKey]: value,
        },
      },
    }));
  };

  const setDomainCharacteristicField = (fieldKey, value) => {
    setForm((current) => {
      const domainVariables = {
        ...(current.domainVariables || current.domain_variables || {}),
      };
      const containerEntry = Object.entries(domainVariables).find(([, variables]) => (
        variables
        && typeof variables === 'object'
        && !Array.isArray(variables)
        && Object.prototype.hasOwnProperty.call(variables, fieldKey)
      ));
      const containerKey = containerEntry?.[0] || 'explore';
      return {
        ...current,
        domainVariables: {
          ...domainVariables,
          [containerKey]: {
            ...(domainVariables[containerKey] || {}),
            [fieldKey]: value,
          },
        },
      };
    });
  };

  const setTelecomScoreField = (score, fieldKey, value) => {
    setForm((current) => {
      const currentScores = normalizeTelecomScoreList(
        current.telecomBehaviorScores?.length
          ? current.telecomBehaviorScores
          : buildPersonaDetail(current)?.telecomBehaviorScores
      );
      const nextScores = currentScores.map((entry) => {
        if (entry.key !== score.key) return entry;
        if (fieldKey === 'score') {
          return { ...entry, score: clampTelecomScore(value, Number(entry.maxScore || 5) || 5) };
        }
        return { ...entry, [fieldKey]: value };
      });
      return { ...current, telecomBehaviorScores: nextScores };
    });
  };

  const isEditFormValid = !isEditMode || visibleEditValues(form).every(hasDetailValue);

  const handleSaveRequest = (event) => {
    event.preventDefault();
    if (!isEditFormValid) {
      setError('내용을 입력해주세요.');
      return;
    }
    if (!isDirty) {
      navigateWithoutBlock(`/personas/${personaId}`, location.state ? { state: location.state } : undefined);
      return;
    }
    setShowSaveConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    if (!isEditFormValid || saving) return;
    setSaving(true);
    setError('');
    try {
      const saved = await personaApi.updatePersona(personaId, buildUpdatePayload(form));
      const normalized = normalizePersonaForEdit(saved);
      setPersona(saved);
      setForm(normalized);
      setBaselinePayload(JSON.stringify(buildUpdatePayload(normalized)));
      setShowSaveConfirmModal(false);
      showToast(`${saved.name || form.name}의 정보가 수정되었습니다.`);
      navigateWithoutBlock(`/personas/${personaId}`, location.state ? { state: location.state } : undefined);
    } catch (err) {
      setError(err.message || 'Persona 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    resetEditFormFromPersona(persona);
    navigateWithoutBlock(`/personas/${personaId}`, location.state ? { state: location.state } : undefined);
  };

  const detailSource = isEditMode ? form : persona;
  const detail = useMemo(() => {
    if (!detailSource) return null;
    const mapped = buildPersonaDetail(detailSource);

    return {
      ...mapped,
      meta: getPersonaMeta(detailSource) || mapped.meta,
    };
  }, [detailSource]);

  const editableSections = (() => {
    if (!isEditMode) return null;
    const jobRows = [
      ['직함', form.title || '', (value) => setField('title', value)],
      ['산업', form.profile?.sector || '', (value) => setProfileField('sector', value)],
      ['직무 영역', form.roleArea || '', (value) => setField('roleArea', value)],
    ];

    return {
      basic: [
        ['나이', form.age, (value) => setField('age', value), 'number'],
        ['세대', form.profile?.generation || '', (value) => setProfileField('generation', value)],
        ['성별', form.gender || '', (value) => setField('gender', value)],
        ['거주', form.currentCity || '', (value) => setField('currentCity', value)],
        ['국가', form.currentCountry || '', (value) => setField('currentCountry', value)],
        ['연소득', form.profile?.income || '', (value) => setProfileField('income', value)],
      ],
      jobTitle: '직업 정보',
      job: jobRows,
      telecomDnaDimensions: TELECOM_DNA_EDIT_SECTIONS.map((section) => ({
        key: section.key,
        title: section.title,
        description: section.description,
        items: section.items.map(([label, fieldKey]) => [
          label,
          getDimensionValue(form.telecomBehaviorDimensions, section.key, fieldKey),
          (value) => setDimensionField(section.key, fieldKey, value),
        ]),
      })),
      telecomLifeCharacteristics: TELECOM_LIFE_EDIT_SECTION ? {
        key: TELECOM_LIFE_EDIT_SECTION.key,
        title: TELECOM_LIFE_EDIT_SECTION.title,
        description: TELECOM_LIFE_EDIT_SECTION.description,
        items: TELECOM_LIFE_EDIT_SECTION.items.map(([label, fieldKey]) => [
          label,
          getDimensionValue(form.telecomBehaviorDimensions, TELECOM_LIFE_EDIT_SECTION.key, fieldKey),
          (value) => setDimensionField(TELECOM_LIFE_EDIT_SECTION.key, fieldKey, value),
        ]),
      } : null,
      domainCharacteristics: buildEditableDomainCharacteristicItems(
        extractDomainCharacteristicsMap(form),
        extractDomainLabelMap(form),
        setDomainCharacteristicField,
      ),
      personality: [
        ['성격', form.personality || '', (value) => setField('personality', value)],
        ['행동 패턴', form.profile?.behaviours || '', (value) => setProfileField('behaviours', value)],
        ['동기부여', form.profile?.motivation || '', (value) => setProfileField('motivation', value)],
        ['가치관/태도', form.profile?.attitudes || '', (value) => setProfileField('attitudes', value)],
      ],
      detailProfile: [
        ['문화적 배경', form.culturalBackground || '', (value) => setField('culturalBackground', value)],
        ['성장 배경', form.profile?.upbringing || '', (value) => setProfileField('upbringing', value)],
        ['이력', form.profile?.biography || '', (value) => setProfileField('biography', value)],
        ['거주이력', form.profile?.residence_history || '', (value) => setProfileField('residence_history', value)],
        ['관심사/취미', form.profile?.interests || '', (value) => setProfileField('interests', value)],
        ['인맥/관계', form.socialContext || '', (value) => setField('socialContext', value)],
        ['선호도', form.profile?.preferences || '', (value) => setProfileField('preferences', value)],
        ['말투/스타일', form.profile?.demeanour || '', (value) => setProfileField('demeanour', value)],
        ['대표 발화', form.profile?.quote || '', (value) => setProfileField('quote', value)],
      ],
    };
  })();

  const content = loading ? (
    <PersonaState type="loading" title="불러오는 중" message="Persona 상세 정보를 가져오고 있습니다." />
  ) : !persona ? (
    <PersonaState
      title="Persona를 찾을 수 없습니다"
      action={<button type="button" className="persona-secondary-button" onClick={() => navigate('/personas')}>목록으로</button>}
    />
  ) : !isEditMode ? (
    <PersonaDetailReadOnlyView
      detailSource={detailSource}
      detail={detail}
      activeTelecomScoreKey={activeTelecomScoreKey}
      onActiveTelecomScoreKeyChange={setActiveTelecomScoreKey}
      actions={isExplorePersona ? (
        <>
          {isAdopted ? (
            <button
              type="button"
              className="persona-detail-action-primary"
              onClick={() => navigate(`/personas/${adoptedPersonaId}`)}
            >
              <ExternalLink size={18} />
              내 퍼소나에서 보기
            </button>
          ) : (
            <button
              type="button"
              className="persona-detail-action-primary"
              onClick={handleAdopt}
              disabled={adopting}
            >
              <BookmarkPlus size={18} />
              {adopting ? '담는 중' : '이용하기'}
            </button>
          )}
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => {
              resetEditFormFromPersona(persona);
              navigate(`/personas/${personaId}/edit`, location.state ? { state: location.state } : undefined);
            }}
          >
            <Edit3 size={18} />
            수정
          </button>
          <button type="button" onClick={handleDelete}>
            <Trash2 size={18} />
            삭제
          </button>
        </>
      )}
    />
  ) : (
    <>
      <section className="persona-detail-profile">
        <div>
          <PersonaAvatar persona={detailSource} size="detail" />
          <div className="persona-detail-identity-main">
            <input
              className="persona-detail-name-input"
              value={form.name}
              maxLength={DETAIL_BASIC_INPUT_MAX_LENGTH}
              placeholder="내용을 입력해주세요."
              onChange={(event) => setField('name', event.target.value.slice(0, DETAIL_BASIC_INPUT_MAX_LENGTH))}
            />
            <input
              className="persona-detail-tag-input"
              value={form.tag || ''}
              maxLength={PERSONA_TAG_MAX_LENGTH}
              placeholder="태그"
              aria-label="퍼소나 태그"
              onChange={(event) => setField('tag', event.target.value.slice(0, PERSONA_TAG_MAX_LENGTH))}
            />
          </div>
          <p>{detail.meta || '프로필 정보 없음'}</p>
        </div>
        <div>
          <button type="button" onClick={handleCancelEdit} disabled={saving}>
            <X size={18} />
            취소
          </button>
          <button type="submit" className="persona-detail-action-primary" disabled={saving || !isEditFormValid}>
            <Save size={18} />
            {saving ? '저장 중' : '수정 완료'}
          </button>
        </div>
      </section>

      <div className="persona-detail-two">
        <InfoCard title="기본 정보" rows={editableSections.basic} editable />
        <InfoCard title={editableSections.jobTitle} rows={editableSections.job} editable />
      </div>

      {detail?.detailMode === 'domain' ? (
        editableSections.domainCharacteristics?.length ? (
          <TextSection
            title={detail.domainCharacteristicsTitle || '도메인 특성'}
            items={editableSections.domainCharacteristics}
            columns={2}
            editable
          />
        ) : null
      ) : detail?.detailMode === 'finance' || detail?.presetId === 'finance' ? (
        <TextSection title="투자 행동 DNA" items={detail.financeDimensions} columns={2} editable={false} />
      ) : (
        <>
          <EditableTelecomDnaSection
            detail={detail}
            sections={editableSections.telecomDnaDimensions}
            activeKey={activeTelecomScoreKey}
            onActiveKeyChange={setActiveTelecomScoreKey}
            onScoreChange={setTelecomScoreField}
          />
          <TelecomLifeCharacteristicsSection
            section={editableSections.telecomLifeCharacteristics}
            editable
          />
        </>
      )}
      <TextSection title="성격 및 행동" items={editableSections.personality} columns={2} editable />
      <TextSection title="상세 프로필" items={editableSections.detailProfile} columns={2} editable />
    </>
  );

  return (
    <B2BLayout
      hideCollapseButton={false}
      defaultCollapsed={true}
      hidePageHeader={true}
      navigationGuard={isEditMode && isDirty ? confirmEditLeave : undefined}
    >
      <main className="persona-detail-screen">
        <header className="persona-detail-title">
          <button
            type="button"
            aria-label={isEditMode ? '목록으로' : '목록으로'}
            onClick={() => {
              if (requestEditLeave({ type: 'return' })) navigateToReturnTarget();
            }}
          >
            <ChevronLeft size={24} strokeWidth={1.8} />
          </button>
          <h1>{isEditMode ? '퍼소나 수정' : '퍼소나 상세'}</h1>
        </header>

        <section className="persona-detail-body">
          <form className="persona-detail-content persona-detail-edit-form" onSubmit={handleSaveRequest}>
            <ErrorBanner message={error} />
            {content}
          </form>
        </section>

        {showDeleteModal ? (
          <DeletePersonaModal
            deleting={deletingPersona}
            onCancel={() => {
              if (deletingPersona) return;
              setShowDeleteModal(false);
            }}
            onConfirm={handleConfirmDelete}
          />
        ) : null}

        {showSaveConfirmModal ? (
          <PersonaConfirmModal
            title="수정을 완료 하시겠습니까?"
            description="퍼소나 정보를 수정하면 이후 생성되는 리서치부터 반영됩니다. 이미 완료된 테스트나 인터뷰 결과 내의 퍼소나 정보는 변경되지 않습니다."
            cancelText="취소하기"
            confirmText={saving ? '수정 중' : '수정하기'}
            disabled={saving}
            onCancel={() => {
              if (saving) return;
              setShowSaveConfirmModal(false);
            }}
            onConfirm={handleConfirmSave}
          />
        ) : null}

        <Toast
          isOpen={toast.isOpen}
          type={toast.type}
          message={toast.message}
          onClose={() => setToast((current) => ({ ...current, isOpen: false }))}
        />
        <PageLeaveConfirmModal
          isOpen={Boolean(pendingEditLeave)}
          message="입력한 수정 내용은 저장되지 않습니다."
          onCancel={handleCancelPendingEditLeave}
          onConfirm={handleConfirmPendingEditLeave}
        />
      </main>
    </B2BLayout>
  );
}

export function PersonaDetailReadOnlyView({
  detailSource,
  detail,
  activeTelecomScoreKey,
  onActiveTelecomScoreKeyChange,
  actions = null,
}) {
  if (!detailSource || !detail) return null;

  return (
    <>
      <section className="persona-detail-profile">
        <div>
          <PersonaAvatar persona={detailSource} size="detail" />
          <div className="persona-detail-identity-main">
            <strong>{detailSource.name || '이름 없는 퍼소나'}</strong>
            <PersonaTagChip tag={detailSource?.tag} />
          </div>
          <p>{detail.meta || '프로필 정보 없음'}</p>
        </div>
        {actions ? <div>{actions}</div> : null}
      </section>

      <div className="persona-detail-two">
        <InfoCard title="기본 정보" rows={detail.basic} />
        <InfoCard title={detail.roleSectionTitle} rows={detail.job} />
      </div>

      {detail?.detailMode === 'domain' ? (
        detail.domainCharacteristics?.length ? (
          <TextSection
            title={detail.domainCharacteristicsTitle || '도메인 특성'}
            items={detail.domainCharacteristics}
            columns={2}
          />
        ) : null
      ) : detail?.detailMode === 'finance' || detail?.presetId === 'finance' ? (
        <TextSection title="투자 행동 DNA" items={detail.financeDimensions} columns={2} />
      ) : (
        <>
          <TelecomDnaSection
            detail={detail}
            activeKey={activeTelecomScoreKey}
            onActiveKeyChange={onActiveTelecomScoreKeyChange}
          />
          <TelecomLifeCharacteristicsSection section={detail.telecomLifeCharacteristicsSection} />
        </>
      )}
      <TextSection title="성격 및 행동" items={detail.personality} columns={2} />
      <TextSection title="상세 프로필" items={detail.detailProfile} columns={2} />
    </>
  );
}

function TelecomLifeCharacteristicsSection({ section, editable = false }) {
  if (!section?.items?.length) return null;

  return (
    <section className="persona-detail-card persona-detail-life">
      <h2>{section.title}</h2>
      <div className="persona-detail-life__grid">
        {section.items.map(([label, value, onChange]) => (
          <article key={label}>
            <h3>{label}</h3>
            {editable ? (
              <EditableDetailTextarea value={value} onChange={onChange} />
            ) : (
              <p>{asDetailText(value, '등록된 정보가 없습니다.')}</p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function EditableTelecomDnaSection({ detail, sections, activeKey, onActiveKeyChange, onScoreChange }) {
  const scores = detail?.telecomBehaviorScores || [];
  if (!scores.length && !sections?.length) return null;

  const selectedScore = scores.find((score) => score.key === activeKey) || scores[0];
  const selectedKey = selectedScore?.key || sections?.[0]?.key;
  const selectedSection = sections?.find((section) => section.key === selectedKey) || sections?.[0];
  const quote = asDetailText(detail?.quote, '');

  return (
    <section className="persona-detail-card persona-detail-dna persona-detail-dna--edit">
      <h2>통신서비스 DNA</h2>
      {quote ? <blockquote>“{quote}”</blockquote> : null}
      <div className="persona-detail-dna__body">
        <div className="persona-detail-dna__scores" aria-label="통신서비스 DNA 스코어 편집">
          {scores.map((score) => {
            const maxScore = Number(score.maxScore || 5);
            const value = clampTelecomScore(score.score, maxScore);
            const width = `${Math.round((value / maxScore) * 100)}%`;
            return (
              <div
                key={score.key}
                className={`persona-detail-dna-score-edit ${score.key === selectedKey ? 'is-active' : ''}`}
                style={{ '--score-color': score.color || '#00cfc0', '--score-width': width }}
              >
                <button type="button" onClick={() => onActiveKeyChange(score.key)}>
                  <span>{score.label}</span>
                </button>
                <input
                  aria-label={`${score.label} 점수`}
                  type="number"
                  min="1"
                  max={maxScore}
                  value={value}
                  onChange={(event) => onScoreChange(score, 'score', event.target.value)}
                  onFocus={() => onActiveKeyChange(score.key)}
                />
                <i aria-hidden="true">
                  {[0, 1, 2, 3, 4, 5].map((point) => (
                    <span key={point} style={{ left: `${point === 0 ? 2 : point === 5 ? 98 : point * 20}%` }} />
                  ))}
                </i>
              </div>
            );
          })}
        </div>
        <div className="persona-detail-dna__insight">
          <div className="persona-detail-dna__tabs" role="tablist" aria-label="통신서비스 DNA 항목 편집">
            {scores.map((score) => (
              <button
                type="button"
                key={score.key}
                role="tab"
                aria-selected={score.key === selectedKey}
                className={`${score.key === selectedKey ? 'is-active' : ''} ${score.key}`}
                onClick={() => onActiveKeyChange(score.key)}
              >
                {score.label}
              </button>
            ))}
          </div>
          {selectedSection ? (
            <div className="persona-detail-dna__copy">
              {selectedSection.description ? (
                <p className="persona-detail-dna__description">{selectedSection.description}</p>
              ) : null}
              {selectedSection.items.map(([label, value, onChange]) => (
                <article key={label}>
                  <h4>{label}</h4>
                  <EditableDetailTextarea value={value} onChange={onChange} />
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function TelecomDnaSection({ detail, activeKey, onActiveKeyChange }) {
  const scores = detail?.telecomBehaviorScores || [];
  const sections = detail?.telecomDimensionSections || [];
  if (!scores.length && !sections.length) return null;

  const selectedScore = scores.find((score) => score.key === activeKey) || scores[0];
  const selectedKey = selectedScore?.key || sections[0]?.key;
  const selectedSection = sections.find((section) => section.key === selectedKey) || sections[0];
  const quote = asDetailText(detail?.quote, '');

  return (
    <section className="persona-detail-card persona-detail-dna">
      <h2>통신서비스 DNA</h2>
      {quote ? <blockquote>“{quote}”</blockquote> : null}
      <div className="persona-detail-dna__body">
        <div className="persona-detail-dna__scores" aria-label="통신서비스 DNA 스코어">
          {scores.map((score) => {
            const maxScore = Number(score.maxScore || 5);
            const value = Math.max(1, Math.min(maxScore, Number(score.score) || 1));
            const width = `${Math.round((value / maxScore) * 100)}%`;
            return (
              <button
                type="button"
                key={score.key}
                className={score.key === selectedKey ? 'is-active' : ''}
                style={{ '--score-color': score.color || '#00cfc0', '--score-width': width }}
                onClick={() => onActiveKeyChange(score.key)}
              >
                <span>
                  <span>{score.label}</span>
                  <strong>{value}점</strong>
                </span>
                <i aria-hidden="true">
                  {[0, 1, 2, 3, 4, 5].map((point) => (
                    <span key={point} style={{ left: `${point === 0 ? 2 : point === 5 ? 98 : point * 20}%` }} />
                  ))}
                </i>
              </button>
            );
          })}
        </div>
        <div className="persona-detail-dna__insight">
          <div className="persona-detail-dna__tabs" role="tablist" aria-label="통신서비스 DNA 항목">
            {scores.map((score) => (
              <button
                type="button"
                key={score.key}
                role="tab"
                aria-selected={score.key === selectedKey}
                className={`${score.key === selectedKey ? 'is-active' : ''} ${score.key}`}
                onClick={() => onActiveKeyChange(score.key)}
              >
                {score.label}
              </button>
            ))}
          </div>
          {selectedSection ? (
            <div className="persona-detail-dna__copy">
              {selectedSection.description ? (
                <p className="persona-detail-dna__description">{selectedSection.description}</p>
              ) : null}
              {selectedSection.items.map(([label, value]) => (
                <article key={label}>
                  <h4>{label}</h4>
                  <p>{asDetailText(value, '등록된 정보가 없습니다.')}</p>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function InfoCard({ title, rows, editable = false }) {
  return (
    <section className="persona-detail-card persona-detail-card--info">
      <h2>{title}</h2>
      <dl>
        {rows.map(([label, value, onChange, type]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>
              {editable ? (
                <input
                  className="persona-detail-field-input"
                  type={type || 'text'}
                  value={asInputValue(value)}
                  maxLength={type === 'number' ? undefined : DETAIL_BASIC_INPUT_MAX_LENGTH}
                  placeholder="내용을 입력해주세요."
                  onChange={(event) => onChange(
                    type === 'number'
                      ? event.target.value
                      : event.target.value.slice(0, DETAIL_BASIC_INPUT_MAX_LENGTH)
                  )}
                />
              ) : (
                asDetailText(value)
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function TextSection({ title, description, items, columns = 1, editable = false }) {
  const rows = Array.isArray(items) ? items : [];
  return (
    <section className="persona-detail-card">
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
      <div className={`persona-detail-text-grid persona-detail-text-grid--${columns}`}>
        {rows.map(([label, value, onChange]) => (
          <article key={label}>
            <h3>{label}</h3>
            {editable ? (
              <EditableDetailTextarea value={value} onChange={onChange} />
            ) : (
              <p>{asDetailText(value, '등록된 정보가 없습니다.')}</p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function EditableDetailTextarea({ value, onChange }) {
  const textValue = asInputValue(value);

  return (
    <Textarea
      value={textValue}
      maxLength={DETAIL_TEXTAREA_MAX_LENGTH}
      showCount
      placeholder="내용을 입력해주세요."
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function PersonaConfirmModal({
  title,
  description,
  cancelText = '취소하기',
  confirmText = '확인',
  disabled = false,
  onCancel,
  onConfirm,
}) {
  return (
    <div className="persona-modal-backdrop" role="presentation">
      <section
        className="persona-delete-modal persona-confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="persona-confirm-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="persona-confirm-modal-title">{title}</h2>
        </header>
        <div className="persona-delete-modal__body">
          <p>{description}</p>
        </div>
        <footer>
          <button type="button" onClick={onCancel} disabled={disabled}>
            {cancelText}
          </button>
          <button type="button" onClick={onConfirm} disabled={disabled}>
            {confirmText}
          </button>
        </footer>
      </section>
    </div>
  );
}

function DeletePersonaModal({ deleting, onCancel, onConfirm }) {
  return (
    <div className="persona-modal-backdrop" role="presentation">
      <section
        className="persona-delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="persona-detail-delete-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="persona-detail-delete-modal-title">선택한 퍼소나를 삭제하시겠습니까?</h2>
        </header>
        <div className="persona-delete-modal__body">
          <p>삭제 시 관련 해당 퍼소나의 정보는 모두 삭제되며</p>
          <p>이 작업은 취소할 수 없습니다.</p>
        </div>
        <footer>
          <button type="button" onClick={onCancel} disabled={deleting}>
            취소하기
          </button>
          <button type="button" onClick={onConfirm} disabled={deleting}>
            {deleting ? '삭제 중' : '삭제하기'}
          </button>
        </footer>
      </section>
    </div>
  );
}

export default PersonaDetailPage;
